import { Divider, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"

import chroma, { Scale } from "chroma-js"
import React, { useState } from "react"
import styled from "styled-components"

const Wrapper = styled.div`
  > header {
    text-align: center;
  }
`

const TitleWrap = styled.div`
  min-height: 68px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const HeatmapWrap = styled.div``

const HeatmapTable = styled.table`
  width: 100%;
`

const TableCell = styled.td`
  padding: 0.5rem;
  white-space: nowrap;
  transition: all 0.4s ease;
`

const HeaderCell = styled(TableCell)<{ isActive: boolean }>`
  padding: 0.5rem;
  cursor: pointer;
  opacity: ${p => (p.isActive ? 1 : 0.4)};
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`

const FirstRowCell = styled(TableCell)<{ isActive: boolean }>`
  text-align: left;
  opacity: ${p => (p.isActive ? 1 : 0.4)};

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`

const ValueCell = styled(TableCell)<{ isActive: boolean }>`
  text-align: right;
  opacity: ${p => (p.isActive ? 1 : 0.3)};
  border: none;
`

type TRowItem = string | number

type TColumnItem = {
  title: string
  children?: TColumnItem[]
}

interface IHeatmapProps {
  /**
   * An optional width for the cells
   */
  cellWidth?: number
  /**
   * A custom Chroma scale function, or the key of a theme colorScale. See Color Palettes.
   */
  colorScale?: Scale | string
  /**
   * Column definition. Objects containing label, and an optional children property,
   * which can contain a second layer of columns. This allows the data columns to be grouped under headings.
   */
  columns: TColumnItem[]
  /**
   * A flag which reduces padding and font size
   */
  dense?: boolean
  /**
   * Row data
   */
  rows: TRowItem[]
  /**
   * Additional styles to be added to the wrapper for layout purposes
   * @default []
   */
  styles: React.CSSProperties
  /**
   * A title to be displayed above the chart
   * @default ""
   */
  title?: string
  /**
   * Min value, used for lower end of scale to color by
   */
  min: number
  /**
   * Min value, used for upper end of scale to color by
   */
  max: number
  /**
   * Styling more subtle, color is shown primarily on border of cells
   * @default false
   */
  outline: boolean
  /**
   * A prefix to add to each cell
   * @default ""
   */
  valuePrefix?: string
  /**
   * A suffix to add to each cell
   * @default ""
   */
  valueSuffix?: string
}

export const Heatmap = ({
  cellWidth,
  colorScale,
  columns = [],
  dense = false,
  outline = false,
  rows = [],
  styles = {},
  title = "",
  min,
  max,
  valueSuffix = "",
  valuePrefix = ""
}: IHeatmapProps) => {
  const theme = useTheme()
  const [activeColumn, setActiveColumn] = useState(-1)
  const [activeRow, setActiveRow] = useState(-1)

  const handleHeaderTapped = (columnIndex: number) => {
    setActiveRow(-1)
    setActiveColumn(c => (columnIndex === activeColumn ? -1 : columnIndex))
  }

  const handleRowTapped = (rowIndex: number) => {
    setActiveColumn(-1)
    setActiveRow(c => (rowIndex === activeRow ? -1 : rowIndex))
  }

  if (min >= max) {
    return <Typography>Invalid min and max</Typography>
  }

  const defaultColorScale = theme.colorScales.financial
  let cellColorScale = defaultColorScale

  if (typeof colorScale === "function") {
    cellColorScale = colorScale
  } else if (
    typeof colorScale === "string" &&
    Object.keys(theme.colorScales).includes(colorScale)
  ) {
    cellColorScale = theme.colorScales[colorScale]
  }

  cellColorScale = cellColorScale.domain([min, max])

  if (!rows) {
    return <p>No Rows</p>
  }

  if (!columns) {
    return <p>No Columns</p>
  }

  const denseStyles = dense ? { fontSize: "0.8rem" } : {}
  const tableStyles = {}

  return (
    <Wrapper style={styles}>
      <header>
        {title ? (
          <>
            <TitleWrap>
              <Typography variant="h4" gutterBottom>
                {title}
              </Typography>
            </TitleWrap>
            <Divider style={{ marginBottom: "1rem" }} />
          </>
        ) : (
          ""
        )}
      </header>
      <HeatmapWrap>
        <HeatmapTable cellSpacing={1} cellPadding={0} style={tableStyles}>
          <thead>
            <tr>
              {columns.map((c: TColumnItem, i: number) => {
                return (
                  <HeaderCell
                    key={`header-cell--${i}`}
                    style={{ textAlign: i ? "right" : "left" }}
                    onClick={() => handleHeaderTapped(i)}
                    isActive={activeColumn === -1 || activeColumn === i}
                    data-testid="header-row"
                  >
                    <Typography style={denseStyles}>{c.title}</Typography>
                  </HeaderCell>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any, i: number) => {
              return (
                <tr key={`row--${i}`}>
                  {row.map((val: number, j: number) =>
                    j ? (
                      <ValueCell
                        key={`cell--${i}_${j}`}
                        isActive={
                          activeColumn > 0
                            ? activeColumn === j
                            : activeRow === -1 || activeRow === i
                        }
                        style={{
                          backgroundColor: outline
                            ? cellColorScale(val).alpha(0.8).css()
                            : cellColorScale(val).css(),
                          border: outline
                            ? `1px solid ${cellColorScale(val).css()}`
                            : "none",
                          color: "black",
                          boxShadow: outline
                            ? `inset 0 0 3px ${chroma(theme.basePalette.bg)
                                .alpha(0.5)
                                .css()}`
                            : "none",
                          width:
                            cellWidth && !Number.isNaN(cellWidth)
                              ? cellWidth
                              : "auto"
                        }}
                      >
                        <Typography
                          style={{
                            ...denseStyles,
                            color:
                              chroma.contrast(
                                cellColorScale(val).hex(),
                                theme.palette.text.primary
                              ) < 4.5
                                ? theme.basePalette.bg
                                : theme.palette.text.primary
                          }}
                        >
                          {j && valuePrefix ? valuePrefix : ""}
                          {val}
                          {j && valueSuffix ? valueSuffix : ""}
                        </Typography>
                      </ValueCell>
                    ) : (
                      <FirstRowCell
                        key={`cell--${i}_${j}`}
                        style={{ backgroundColor: "none" }}
                        isActive={activeRow === -1 || activeRow === i}
                        onClick={() => handleRowTapped(i)}
                      >
                        <Typography style={denseStyles}>{val}</Typography>
                      </FirstRowCell>
                    )
                  )}
                </tr>
              )
            })}
          </tbody>
        </HeatmapTable>
      </HeatmapWrap>
    </Wrapper>
  )
}

export default Heatmap
